/**
 * PrintReportShell — shared A4 print-report chrome + trigger button
 * used by every tool that needs a PDF export.
 *
 * The tool renders its normal on-screen result view inside this shell.
 * On tap of the "PRINT REPORT" button, `window.print()` fires — the
 * embedded stylesheet:
 *   • Hides everything with `no-print` class (nav chrome, side panels)
 *   • Forces light theme so ink cost stays sane
 *   • Applies A4 page geometry with sensible margins
 *   • Reveals anything with `print-only` class (large report banner
 *     with project/date/surveyor that's normally hidden on screen)
 *
 * All tools use this shell so the report look-and-feel is identical
 * across the app — the same header block, the same margins, the
 * same "PASS/FAIL stamp in the top right" convention.
 */
import React from "react";
import { Printer } from "lucide-react";

export function PrintReportShell({
  toolName,           // e.g. "Level Check"
  sessionName,        // e.g. "N3 header beam"
  passFail,           // "PASS" | "FAIL" | null (hides the stamp)
  headline,           // one-line kpi shown next to the stamp
  surveyor,           // signed-in user's display name
  children,           // tool-specific report body (table, chart, photo…)
  disabled = false,
}) {
  const stampColour = passFail === "PASS" ? "#2E7D32"
                    : passFail === "FAIL" ? "#C62828"
                    : "#4A5054";
  return (
    <>
      {/* Print stylesheet — scoped by class, applied globally on print
          because @media print rules cascade across the document. */}
      <style>{`
        @media print {
          @page { size: A4; margin: 14mm 12mm 20mm 12mm; }
          html, body { background: #ffffff !important; color: #0A0B0E !important; }
          .no-print, [data-testid="tool-split-right"], header { display: none !important; }
          .print-only { display: block !important; }
          .print-report table { color: #0A0B0E !important; border-color: #C0C4CA !important; }
          .print-report table thead tr { background: #E5E7EB !important; color: #0A0B0E !important; }
          .print-report .kpi-tile { background: #F7F8FA !important; border-color: #C0C4CA !important; }
        }
        .print-only { display: none; }
      `}</style>

      {/* Print-only banner — surveyor + tool + date + pass/fail stamp.
          Rendered above the tool body so page 1 of the PDF always
          leads with identity + result. */}
      <div className="print-only print-report" style={{ marginBottom: "12mm" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                      borderBottom: "2px solid #0A0B0E", paddingBottom: "6mm" }}>
          <div>
            <div style={{ fontSize: "10pt", letterSpacing: "0.2em", color: "#4A5054", textTransform: "uppercase" }}>
              FabAxis · Field Report
            </div>
            <div style={{ fontSize: "24pt", fontWeight: 900, marginTop: "2mm" }}>
              {toolName}
            </div>
            <div style={{ fontSize: "13pt", color: "#4A5054", marginTop: "1mm" }}>
              {sessionName || "—"}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            {passFail && (
              <div style={{
                fontSize: "22pt", fontWeight: 900, letterSpacing: "0.06em",
                color: stampColour, border: `3px solid ${stampColour}`,
                padding: "2mm 6mm", display: "inline-block",
              }}>
                {passFail}
              </div>
            )}
            <div style={{ fontSize: "11pt", marginTop: "3mm" }}>
              {headline || ""}
            </div>
            <div style={{ fontSize: "9pt", color: "#4A5054", marginTop: "4mm" }}>
              {new Date().toLocaleString()}
            </div>
            <div style={{ fontSize: "9pt", color: "#4A5054" }}>
              Surveyor: {surveyor || "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Tool body — normal on-screen content, appears on print too. */}
      <div className="print-report">
        {children}
      </div>
    </>
  );
}

/**
 * PrintReportButton — the trigger. Emitted separately so a tool can
 * place it inline with its other action buttons (Back / Add / etc.)
 * rather than inside the shell.
 */
export function PrintReportButton({ onClick = () => window.print(), disabled = false, testid = "print-report-btn" }) {
  return (
    <button
      type="button"
      data-testid={testid}
      disabled={disabled}
      onClick={onClick}
      className="flex items-center gap-2 rounded-md border border-[#8DC63F]/40 bg-[#8DC63F]/10 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#8DC63F] hover:bg-[#8DC63F]/20 disabled:opacity-40 no-print"
      style={{ fontFamily: "Chakra Petch, sans-serif" }}
    >
      <Printer size={14} strokeWidth={2.5} />
      Print report
    </button>
  );
}

export default PrintReportShell;
